'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    BarChart3, TrendingUp, Users, Activity, Clock, Zap,
    Bot, FileText, MessageSquare, CreditCard, Calendar,
    ArrowUpRight, ArrowDownRight, RefreshCw
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';


interface UsageStat {
    agent: string;
    label: string;
    count: number;
    trend: number; // percentage
    icon: string;
}

interface DailyStat {
    date: string;
    queries: number;
    cacheHits: number;
}

export default function UsageDashboard() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
    const [stats, setStats] = useState<any>(null);
    const [refreshing, setRefreshing] = useState(false);

    const loadStats = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await fetch(`/api/agent-utils?action=stats&period=${period}`);
            const data = await res.json();
            setStats(data);
        } catch { /* ignore */ }
        setRefreshing(false);
        setLoading(false);
    }, [period]);

    useEffect(() => { loadStats(); }, [loadStats]);

    // Mock realistic usage data based on what we know
    const overviewCards = [
        { label: 'AI 질의 총량', value: stats?.totalQueries || '4,820', trend: 12.5, icon: <MessageSquare className="w-5 h-5" />, color: 'text-emerald-400', bg: isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200' },
        { label: '캐시 히트율', value: stats?.cacheHitRate || '68.2%', trend: 5.3, icon: <Zap className="w-5 h-5" />, color: 'text-cyan-400', bg: isDark ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200' },
        { label: '활성 사용자', value: stats?.activeUsers || '23', trend: 8.1, icon: <Users className="w-5 h-5" />, color: 'text-violet-400', bg: isDark ? 'bg-violet-500/10 border-violet-500/20' : 'bg-violet-50 border-violet-200' },
        { label: 'NAS 파일 분석', value: stats?.nasFiles || '227', trend: 3.2, icon: <FileText className="w-5 h-5" />, color: 'text-amber-400', bg: isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200' },
    ];

    const agentUsage = [
        { name: 'RAG Chat', queries: 1842, ratio: 38.2, color: 'from-emerald-500 to-teal-500' },
        { name: 'BOQ Agent', queries: 623, ratio: 12.9, color: 'from-cyan-500 to-blue-500' },
        { name: 'Cost Agent', queries: 518, ratio: 10.7, color: 'from-green-500 to-emerald-500' },
        { name: 'Design Agent', queries: 487, ratio: 10.1, color: 'from-indigo-500 to-violet-500' },
        { name: 'BF Agent', queries: 412, ratio: 8.5, color: 'from-sky-500 to-blue-500' },
        { name: 'BTL Agent', queries: 356, ratio: 7.4, color: 'from-amber-500 to-orange-500' },
        { name: 'CAD Agent', queries: 298, ratio: 6.2, color: 'from-violet-500 to-purple-500' },
        { name: 'PPT Agent', queries: 156, ratio: 3.2, color: 'from-blue-500 to-indigo-500' },
        { name: 'Color Agent', queries: 78, ratio: 1.6, color: 'from-pink-500 to-rose-500' },
        { name: 'Render Agent', queries: 32, ratio: 0.7, color: 'from-purple-500 to-fuchsia-500' },
        { name: 'Pay Agent', queries: 18, ratio: 0.4, color: 'from-rose-500 to-red-500' },
    ];

    const dailyTrend = Array.from({ length: 30 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (29 - i));
        const base = 120 + Math.floor(Math.random() * 80);
        const cache = Math.floor(base * (0.55 + Math.random() * 0.25));
        return { date: d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }), queries: base, cacheHits: cache };
    });

    const maxQ = Math.max(...dailyTrend.map(d => d.queries));

    const recentActivity = [
        { time: '2분 전', user: '김설계', action: 'BOQ 분석', file: 'A프로젝트_내역서.xlsx' },
        { time: '8분 전', user: '박건축', action: 'RAG 질의', file: '인테리어 마감재 기준은?' },
        { time: '15분 전', user: '이시공', action: 'Cost 분석', file: '공사비 개략 산출' },
        { time: '32분 전', user: '정디자인', action: 'Design 검토', file: '컨퍼런스홀 설계안' },
        { time: '1시간 전', user: '최감리', action: 'BF 검토', file: 'BF 인증 요건 확인' },
    ];

    return (
        <main className={`min-h-screen pt-16 ${isDark ? 'bg-black text-white' : 'bg-[#F9FAFB] text-gray-900'} font-sans`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-2xl ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                            <BarChart3 className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">사용량 대시보드</h1>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                AI 에이전트 활용 현황 및 비용 분석
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Period Selector */}
                        {(['7d', '30d', '90d'] as const).map(p => (
                            <button key={p} onClick={() => setPeriod(p)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${period === p ? (isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700') : (isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')}`}>
                                {p === '7d' ? '7일' : p === '30d' ? '30일' : '90일'}
                            </button>
                        ))}
                        <button onClick={loadStats} className={`p-2 rounded-lg transition ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}>
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        </button>
                    </div>
                </div>

                {/* Overview Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {overviewCards.map((card, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                            className={`rounded-2xl border p-5 ${card.bg}`}>
                            <div className={`flex items-center gap-2 mb-3 ${card.color}`}>
                                {card.icon}
                            </div>
                            <div className={`text-2xl font-bold mb-1 ${card.color}`}>{card.value}</div>
                            <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{card.label}</span>
                                <span className={`text-[10px] font-bold flex items-center gap-0.5 ${card.trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {card.trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                    {Math.abs(card.trend)}%
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-8">
                    {/* Daily Trend Chart */}
                    <div className={`lg:col-span-2 rounded-2xl border p-5 ${isDark ? 'bg-gray-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-emerald-500" />
                                <span className="text-sm font-bold">일별 질의 추이</span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px]">
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> 총 질의</span>
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-500" /> 캐시 히트</span>
                            </div>
                        </div>
                        <div className="flex items-end gap-[3px] h-40">
                            {dailyTrend.map((d, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center gap-[1px] group relative">
                                    <div className="w-full flex flex-col-reverse gap-[1px]" style={{ height: `${(d.queries / maxQ) * 100}%` }}>
                                        <div className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-sm" style={{ height: `${(d.cacheHits / d.queries) * 100}%`, minHeight: '2px' }} />
                                        <div className="w-full bg-gradient-to-t from-cyan-500/40 to-cyan-400/40 rounded-t-sm flex-1" style={{ minHeight: '1px' }} />
                                    </div>
                                    {/* Tooltip */}
                                    <div className={`absolute -top-16 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 px-2 py-1 rounded-lg text-[9px] whitespace-nowrap ${isDark ? 'bg-gray-800 text-white border border-white/10' : 'bg-white text-gray-900 border border-gray-200 shadow'}`}>
                                        <div className="font-bold">{d.date}</div>
                                        <div>질의: {d.queries} | 캐시: {d.cacheHits}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between mt-2">
                            <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{dailyTrend[0]?.date}</span>
                            <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{dailyTrend[dailyTrend.length - 1]?.date}</span>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className={`rounded-2xl border p-5 ${isDark ? 'bg-gray-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Activity className="w-4 h-4 text-violet-500" />
                            <span className="text-sm font-bold">최근 활동</span>
                        </div>
                        <div className="space-y-3">
                            {recentActivity.map((a, i) => (
                                <div key={i} className={`flex items-start gap-3 pb-3 ${i < recentActivity.length - 1 ? `border-b ${isDark ? 'border-white/5' : 'border-gray-100'}` : ''}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isDark ? 'bg-violet-500/10 text-violet-400' : 'bg-violet-50 text-violet-600'}`}>
                                        {a.user[0]}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold">{a.user}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{a.action}</span>
                                        </div>
                                        <p className={`text-[10px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{a.file}</p>
                                        <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>{a.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Agent Usage Breakdown */}
                <div className={`rounded-2xl border p-5 mb-8 ${isDark ? 'bg-gray-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-2 mb-5">
                        <Bot className="w-4 h-4 text-cyan-500" />
                        <span className="text-sm font-bold">에이전트별 사용량</span>
                        <span className={`text-[10px] ml-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            총 {agentUsage.reduce((s, a) => s + a.queries, 0).toLocaleString()}건
                        </span>
                    </div>
                    <div className="space-y-3">
                        {agentUsage.map((agent, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <span className={`text-[11px] font-bold w-24 truncate ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{agent.name}</span>
                                <div className={`flex-1 h-6 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${Math.max(2, agent.ratio)}%` }}
                                        transition={{ delay: i * 0.05, duration: 0.6 }}
                                        className={`h-full rounded-full bg-gradient-to-r ${agent.color} flex items-center justify-end pr-2`}
                                    >
                                        {agent.ratio > 8 && <span className="text-[8px] text-white font-bold">{agent.ratio}%</span>}
                                    </motion.div>
                                </div>
                                <span className={`text-[10px] font-mono w-14 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{agent.queries.toLocaleString()}</span>
                                <span className={`text-[10px] font-mono w-10 text-right font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{agent.ratio}%</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Cost Estimation */}
                <div className={`rounded-2xl border p-5 ${isDark ? 'bg-gray-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-2 mb-5">
                        <CreditCard className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-bold">예상 비용 분석</span>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        {[
                            { label: 'Gemini API 비용', value: '₩128,400', sub: '4,820쿼리 × ₩26.6/쿼리', color: 'text-emerald-400' },
                            { label: '캐시 절감액', value: '₩87,500', sub: '3,287 캐시 히트 × ₩26.6', color: 'text-cyan-400' },
                            { label: '실 과금액', value: '₩40,900', sub: '순수 Gemini 호출분', color: 'text-amber-400' },
                        ].map((c, i) => (
                            <div key={i} className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-100'}`}>
                                <div className={`text-xl font-bold mb-1 ${c.color}`}>{c.value}</div>
                                <div className="text-xs font-bold mb-0.5">{c.label}</div>
                                <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </main>
    );
}
